/**
 * Local echo for messages the widget has sent but the server has not confirmed yet.
 *
 * POST /widgets/query answers with an empty ack, so nothing can be rendered from it. The record
 * arrives later over the socket, once the backend has persisted it — behind a couple of
 * conversation writes and, on human handoff, an agent lookup and a push notification. Until then
 * the input has been cleared and the message is nowhere to be seen.
 *
 * An echo is matched to its server record by the correlation id the widget mints per message and
 * sends as x-correlation-id; the backend persists it on the message doc and emits the whole
 * record, so the join is exact rather than a guess at text and timing.
 */

/**
 * Build the echo for a message being sent.
 *
 * The `query` shape mirrors what the server persists, so Sender renders an echo and a real
 * record identically and the swap between them is invisible.
 */
export const createPendingRecord = ({ correlationId, text, conversationId = null }) => ({
	_id: `pending-${correlationId}`,
	correlationId,
	conversationId: conversationId ?? null,
	query: { type: "msg", config: { message: text, link: "" } },
	createdAt: new Date().toISOString(),
	pending: true,
});

/**
 * Server records followed by any echo still waiting for its record.
 *
 * An echo drops out as soon as a record carrying its correlation id appears, whatever brought it
 * in — the socket, a refetch, the reconnect sync — so no caller has to remember to clean up.
 * Echoes from another conversation are held back, except that a first message has no conversation
 * id yet and neither does a freshly opened widget.
 */
export const mergePendingRecords = (records, pendingRecords, currentConversationId = null) => {
	const confirmed = Array.isArray(records) ? records : [];
	if (!Array.isArray(pendingRecords) || pendingRecords.length === 0) return confirmed;

	const seen = new Set(confirmed.map(r => r?.correlationId).filter(Boolean));
	const unconfirmed = pendingRecords.filter(r =>
		r?.correlationId &&
		!seen.has(r.correlationId) &&
		(r.conversationId == null || currentConversationId == null || r.conversationId === currentConversationId)
	);

	return unconfirmed.length > 0 ? [ ...confirmed, ...unconfirmed ] : confirmed;
};
